import { FlightSearchResponse } from 'ts/flight/interfaces/FlightSearch';
import { useEffect, useState } from 'react';
import { FlightSearch } from 'ts/flight/interfaces/FlightSearch';
import useRecaptcha from 'hooks/useRecaptcha';
import { beginFlightSearch, getFlights } from 'services/flight';
import { delay } from 'utils/utils';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import { FlightTopBarState } from 'ts/flight/classes/FlightTopBarStates';
import { getFilteredFlights, initFlights } from 'utils/flight/flight';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import * as idb from 'utils/shared/idbUrl';

const useFlightResult = (
  abortController: AbortController,
  sessionStorageKey: string,
  onChangeTopBarState: (state: FlightTopBarState) => void,
  idbKey: string,
  topBarState: FlightTopBarState,
  searchParams?: SearchFlightModel,
  skipHistorySave?: boolean
) => {
  const [response, setResponse] = useState<FlightSearchResponse>();
  const [flights, setFlights] = useState<FlightSearch[]>();
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [filter, setFilter] = useState<FlightFilterModel>();

  useEffect(() => {
    (async () => {
      const state = await idb.getPageState(idbKey);
      if (state && state.flights1?.length) return;

      if (!isRecaptchaReady || !searchParams) return;
      setFlights(undefined);
      setResponse(undefined);
      const recaptchaToken = await handleRecapcha();
      if (!recaptchaToken) return;

      const searchToken = await beginFlightSearch(
        { ...searchParams, recaptchaToken, skipHistorySave },
        abortController
      );

      if (searchToken) {
        sessionStorage.setItem(sessionStorageKey, searchToken);
        await delay(4000);
        await getFlightRecursive(0, searchToken);
      }
    })();
  }, [searchParams, isRecaptchaReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (flights && filter) {
      const newFlights = getFilteredFlights(flights, filter);

      onChangeTopBarState({
        ...topBarState,
        visibleFlightsCount: newFlights.filter((flight) => flight.visible).length,
      });

      setFlights(newFlights);
    }
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFlightRecursive = async (skip: number, searchToken: string) => {
    const newResponse = await getFlights({
      body: { searchToken, skip },
      controller: abortController,
    });

    if (newResponse && !newResponse.sessionExpired) {
      setResponse((prev) => {
        if (newResponse?.flights) {
          setFlights((currentFlights) => {
            const newFlights = [
              ...(currentFlights || []),
              ...initFlights(newResponse.flights),
            ].sort((a, b) => {
              return a.priceGross - b.priceGross;
            });

            setFilter((prevFilter) => {
              const newFilter = new FlightFilterModel(newFlights, prevFilter);

              return newFilter;
            });

            onChangeTopBarState({
              ...topBarState,
              visibleFlightsCount: newFlights.length,
              totalFlightsCount: newFlights.length,
              pollingFinished: newResponse.poolingFinished,
            });

            if (!newResponse.poolingFinished) {
              (async () => {
                await delay(2000);
                getFlightRecursive(newFlights.length, searchToken!);
              })();
            }

            return newFlights;
          });
        }
        return prev;
      });
    } else if (response) {
      setResponse(newResponse);

      onChangeTopBarState({
        ...topBarState,
        pollingFinished: true,
        sessionExpired: true,
      });
      sessionStorage.clear();
    }
  };

  return { response, flights, setResponse, setFlights, filter, setFilter };
};

export default useFlightResult;
